/**
 * Synchronized device configuration — mapping layer between the backend `device_setting`
 * EAV tables and the app's local `settings.*` object.
 *
 * The backend is the source of truth: operations writes a value, a trigger pushes a
 * `reconfigure` action, and the tablet re-reads and re-applies. IndexedDB is only an offline
 * cache. See the handoff doc from Eric (`device-setting-webapp-handoff.md`).
 *
 * Key facts this module encapsulates:
 *  - `device_setting.value` is ALWAYS a varchar; the real type lives in `setting_parameter.type`,
 *    so we cast here (`'true'`/`'false'` -> boolean, `'0.5'` -> number, strings as-is).
 *  - `value === null` means "use the default" (for `selected_camera_id`, null = Automatic).
 *  - the app stores photo quality as a pixel width (`settings.resolution`) but the DB stores a
 *    tier (`low`/`medium`/`high`); we translate both ways.
 */

// setting_parameter.parameter values (snake_case, as stored in the DB).
export const PARAM = {
  IMMERSIVE_VIEWPORT: 'immersive_viewport',
  FLIP_VERTICALLY: 'flip_vertically',
  SELECTED_CAMERA_ID: 'selected_camera_id',
  PHOTO_QUALITY: 'photo_quality',
  CAPTURE_METHOD: 'capture_method',
  BLACK_IMAGE_DETECTION: 'black_image_detection',
  EXPOSURE_COMPENSATION: 'exposure_compensation',
};

// Local defaults — a safety net mirroring the DB seed (`setting_value.is_default`). Rows always
// exist (seeded at device creation, existing devices backfilled), so in practice none are missing.
const DEFAULTS = {
  [PARAM.IMMERSIVE_VIEWPORT]: true,
  [PARAM.FLIP_VERTICALLY]: false,
  [PARAM.SELECTED_CAMERA_ID]: null,
  [PARAM.PHOTO_QUALITY]: 'medium',
  [PARAM.CAPTURE_METHOD]: 'auto',
  [PARAM.BLACK_IMAGE_DETECTION]: true,
  [PARAM.EXPOSURE_COMPENSATION]: 0,
};

// photo_quality tier <-> pixel width (app keeps px in settings.resolution).
const QUALITY_TO_PX = { low: 1600, medium: 1920, high: 2560 };
const PX_TO_QUALITY = { 1600: 'low', 1920: 'medium', 2560: 'high' };
const DEFAULT_PX = QUALITY_TO_PX[DEFAULTS[PARAM.PHOTO_QUALITY]];

// exposure_compensation slider domain shared with the DB allowed-value set (`setting_value`):
// -3.0 … 3.0 in 0.1 steps. The sensor's real range is clamped separately at apply time.
const EXPOSURE_MIN = -3;
const EXPOSURE_MAX = 3;

// Cast a stored varchar `value` to the JS type the app expects, per parameter.
const CAST = {
  [PARAM.IMMERSIVE_VIEWPORT]: (v) => v === 'true',
  [PARAM.FLIP_VERTICALLY]: (v) => v === 'true',
  [PARAM.BLACK_IMAGE_DETECTION]: (v) => v === 'true',
  [PARAM.EXPOSURE_COMPENSATION]: (v) => parseFloat(v),
  [PARAM.SELECTED_CAMERA_ID]: (v) => v, // string | null
  [PARAM.PHOTO_QUALITY]: (v) => v, // string tier
  [PARAM.CAPTURE_METHOD]: (v) => v, // string ('auto' | 'imageCapture' | 'canvas')
};

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

// Serialize an app `settings.*` value back to the varchar the DB expects for its parameter.
// The returned string MUST match an allowed `setting_value` row (a composite FK validates non-null
// values); `null` is always accepted and means "use the default".
const SERIALIZE = {
  immersiveViewport: (v) => (v ? 'true' : 'false'),
  flipVertically: (v) => (v ? 'true' : 'false'),
  blackImageDetection: (v) => (v ? 'true' : 'false'),
  cameraCaptureMethod: (v) => String(v),
  resolution: (v) => PX_TO_QUALITY[v] ?? DEFAULTS[PARAM.PHOTO_QUALITY], // px -> tier
  cameraExposureComp: (v) => {
    // Clamp to the canonical domain and snap to the 0.1 grid, then format with one decimal so the
    // string matches the enumerated `setting_value` rows (e.g. 0 -> '0.0', 0.5 -> '0.5', -3 -> '-3.0').
    const snapped = Math.round(clamp(Number(v) || 0, EXPOSURE_MIN, EXPOSURE_MAX) * 10) / 10;
    return snapped.toFixed(1);
  },
};

// app `settings.*` key -> DB parameter, for the params the tablet WRITES back.
// `selected_camera_id` is deliberately omitted: it has no `setting_value` rows, so the composite
// (parameter, value) FK rejects any non-null value, and camera deviceIds are per-browser / not
// portable anyway. We keep it device-local (persisted only in IndexedDB, never synced). See
// reduceDeviceSettings for the matching read-side omission.
const KEY_TO_PARAM = {
  immersiveViewport: PARAM.IMMERSIVE_VIEWPORT,
  flipVertically: PARAM.FLIP_VERTICALLY,
  resolution: PARAM.PHOTO_QUALITY,
  cameraCaptureMethod: PARAM.CAPTURE_METHOD,
  blackImageDetection: PARAM.BLACK_IMAGE_DETECTION,
  cameraExposureComp: PARAM.EXPOSURE_COMPENSATION,
};

/**
 * Reduce the `device_setting` rows returned by GraphQL into the partial `settings.*` object the
 * app consumes. A missing row or `value === null` falls back to the local default.
 *
 * @param {Array<{parameter: string, value: string|null}>} rows
 * @returns {{immersiveViewport: boolean, flipVertically: boolean, selectedCameraId: string|null,
 *   resolution: number, cameraCaptureMethod: string, blackImageDetection: boolean,
 *   cameraExposureComp: number}}
 */
export function reduceDeviceSettings(rows = []) {
  const cfg = { ...DEFAULTS };
  for (const { parameter, value } of rows || []) {
    if (!(parameter in DEFAULTS)) continue; // ignore params we don't map yet (forward-compatible)
    cfg[parameter] = value == null ? DEFAULTS[parameter] : CAST[parameter](value);
  }
  // NOTE: `selectedCameraId` is intentionally not returned — it stays device-local (see KEY_TO_PARAM).
  // Including it here would let every reconfigure/login reset the operator's local camera pick to the
  // DB's only possible value (NULL = Automatic), since a specific id can't be stored backend-side.
  // NOTE: an unknown photo_quality tier falls back to the default px (forward-compatible — a new tier
  // ships with a one-line QUALITY_TO_PX addition, no DB migration; see handoff §5).
  return {
    immersiveViewport: cfg[PARAM.IMMERSIVE_VIEWPORT],
    flipVertically: cfg[PARAM.FLIP_VERTICALLY],
    resolution: QUALITY_TO_PX[cfg[PARAM.PHOTO_QUALITY]] ?? DEFAULT_PX,
    cameraCaptureMethod: cfg[PARAM.CAPTURE_METHOD],
    blackImageDetection: cfg[PARAM.BLACK_IMAGE_DETECTION],
    cameraExposureComp: cfg[PARAM.EXPOSURE_COMPENSATION],
  };
}

/**
 * Diff the previous vs. next settings and return the backend writes needed for the changed
 * synchronized params. Only the 7 camera keys are considered; every other key is local-only.
 *
 * @param {object} oldSettings
 * @param {object} newSettings
 * @returns {Array<{parameter: string, value: string|null}>}
 */
export function diffSettingsToParameterWrites(oldSettings = {}, newSettings = {}) {
  const writes = [];
  for (const key of Object.keys(KEY_TO_PARAM)) {
    if (!(key in newSettings)) continue;
    if (oldSettings[key] === newSettings[key]) continue;
    writes.push({ parameter: KEY_TO_PARAM[key], value: SERIALIZE[key](newSettings[key]) });
  }
  return writes;
}

/**
 * One-time migration plan for the first login after this feature ships. The backend rows were
 * seeded with DEFAULTS, but the tablet's real config lives only in its local cache — so we upload
 * the local value for any synchronized param the backend hasn't been explicitly set on yet.
 *
 * Rule per param: migrate the local value up ONLY when the backend row is still at its seeded
 * default (untouched) AND the local value maps to a different stored value. If ops already set a
 * non-default backend value, the backend wins (an explicit remote config is never clobbered).
 *
 * @param {object} localSettings - the tablet's cached `settings.*` object (source of the real config)
 * @param {Array<{parameter: string, value: string|null}>} backendRows - current device_setting rows
 * @returns {{merged: object, writes: Array<{parameter: string, value: string|null}>}}
 *   `merged` = the reconciled settings to apply locally; `writes` = the backend UPDATEs to push.
 */
export function planFirstRunMigration(localSettings = {}, backendRows = []) {
  const backend = reduceDeviceSettings(backendRows);
  const appDefaults = reduceDeviceSettings([]);
  const merged = { ...backend };
  const writes = [];
  for (const key of Object.keys(KEY_TO_PARAM)) {
    const def = appDefaults[key];
    const hasLocal =
      localSettings &&
      Object.prototype.hasOwnProperty.call(localSettings, key) &&
      localSettings[key] != null;
    const locVal = hasLocal ? localSettings[key] : def;
    // Backend still at its seeded default AND local maps to a different stored value -> migrate up.
    if (backend[key] === def && SERIALIZE[key](locVal) !== SERIALIZE[key](def)) {
      merged[key] = locVal;
      writes.push({ parameter: KEY_TO_PARAM[key], value: SERIALIZE[key](locVal) });
    }
  }
  return { merged, writes };
}
